<template>
  <div class="storage-page">
  <PageLayout :title="$t('storage.title')" :subtitle="$t('storage.subtitle')">
    <template #actions>
      <label class="btn btn-primary mb-0" :class="{ disabled: uploading }">
        <i :class="uploading ? 'fas fa-spinner fa-spin mr-1' : 'fas fa-cloud-upload-alt mr-1'"></i>
        {{ uploading ? $t('storage.uploading') : $t('storage.upload_files') }}
        <input
          type="file"
          class="storage-upload-input"
          multiple
          accept="image/*,video/*"
          :disabled="uploading"
          @change="onUpload"
        />
      </label>
    </template>

    <div class="storage-usage">
      <div class="storage-usage__body">
        <div class="storage-usage__top">
          <span class="storage-usage__label">{{ $t('storage.usage_label') }}</span>
          <span class="storage-usage__meta">
            {{ formatBytes(usage.used_bytes) }}
            <template v-if="usage.limit_bytes > 0">{{ $t('storage.of_used', { size: formatBytes(usage.limit_bytes) }) }}</template>
            <template v-else>{{ $t('storage.used_unlimited') }}</template>
            <span v-if="usage.file_count" class="storage-usage__count">· {{ $t('storage.files_count', { count: usage.file_count }) }}</span>
          </span>
        </div>
        <div class="storage-usage__track" role="progressbar" :aria-valuenow="usagePercent" aria-valuemin="0" aria-valuemax="100">
          <div class="storage-usage__fill" :style="{ width: usagePercent + '%' }" :class="usageBarClass" />
        </div>
      </div>
      <router-link v-if="usage.limit_bytes > 0" to="/billing" class="storage-usage__upgrade">
        {{ $t('storage.get_more') }}
      </router-link>
    </div>

    <ContentCard card-class="storage-card p-0 overflow-hidden">
      <div
        class="storage-panel"
        :class="{ 'storage-panel--drag': dragOver }"
        @dragenter.prevent="onDragEnter"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
      >
        <div class="storage-toolbar">
          <div class="storage-filters" role="tablist" :aria-label="$t('storage.filter_label')">
            <button
              v-for="tab in sourceTabs"
              :key="tab.id"
              type="button"
              role="tab"
              class="storage-filter"
              :class="{ 'storage-filter--active': source === tab.id }"
              :aria-selected="source === tab.id"
              @click="setSource(tab.id)"
            >
              <i :class="tab.icon" aria-hidden="true"></i>
              <span>{{ tab.label }}</span>
              <span v-if="tab.id === source && total > 0" class="storage-filter__count">{{ total }}</span>
            </button>
          </div>

          <div class="storage-toolbar__row">
            <div class="storage-search">
              <i class="fas fa-search" aria-hidden="true"></i>
              <input
                v-model="query"
                type="search"
                :placeholder="$t('storage.search_placeholder')"
                :aria-label="$t('storage.search_placeholder')"
              />
              <button
                v-if="query"
                type="button"
                class="storage-search__clear"
                :aria-label="$t('storage.clear_search')"
                @click="query = ''"
              >
                <i class="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>

            <div class="storage-toolbar__actions">
              <button
                v-if="total > 0"
                type="button"
                class="btn btn-sm btn-outline-danger"
                :disabled="deletingAll || uploading"
                @click="onDeleteAll"
              >
                <i :class="deletingAll ? 'fas fa-spinner fa-spin mr-1' : 'fas fa-trash-alt mr-1'"></i>
                {{ $t('storage.delete_all') }}
              </button>

              <div class="storage-sort">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary storage-sort__trigger"
                  :aria-expanded="sortOpen"
                  @click="sortOpen = !sortOpen"
                >
                  <i class="fas fa-sort-amount-down mr-1"></i>
                  {{ activeSortLabel }}
                </button>
                <div v-if="sortOpen" class="storage-menu">
                  <button
                    v-for="opt in sortOptions"
                    :key="opt.id"
                    type="button"
                    class="storage-menu__item"
                    :class="{ 'storage-menu__item--active': sortBy === opt.id }"
                    @click="setSort(opt.id)"
                  >
                    {{ opt.label }}
                  </button>
                </div>
              </div>

              <div class="btn-group btn-group-sm" role="group" :aria-label="$t('storage.view_mode')">
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  :class="{ active: viewMode === 'grid' }"
                  :title="$t('storage.grid_view')"
                  @click="viewMode = 'grid'"
                >
                  <i class="fas fa-th-large"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  :class="{ active: viewMode === 'list' }"
                  :title="$t('storage.list_view')"
                  @click="viewMode = 'list'"
                >
                  <i class="fas fa-list"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="dragOver" class="storage-drop" aria-hidden="true">
          <i class="fas fa-cloud-upload-alt"></i>
          <p>{{ $t('storage.drop_upload') }}</p>
        </div>

        <div class="storage-body">
          <div v-if="loading && !files.length" class="storage-state" role="status" aria-live="polite" aria-busy="true">
            <i class="fas fa-spinner fa-spin"></i>
            <p>{{ $t('storage.loading_files') }}</p>
          </div>

          <div v-else-if="!displayFiles.length" class="storage-state">
            <div class="storage-state__icon" aria-hidden="true">
              <i :class="emptyIcon"></i>
            </div>
            <h5>{{ query ? $t('storage.no_matching') : emptyTitle }}</h5>
            <p>{{ query ? $t('storage.try_different_search') : emptyHint }}</p>
            <label v-if="!query && source !== 'canva' && source !== 'studio_api'" class="btn btn-primary btn-sm mt-3 mb-0">
              <i class="fas fa-cloud-upload-alt mr-1"></i>
              {{ $t('storage.upload_files') }}
              <input
                type="file"
                class="storage-upload-input"
                multiple
                accept="image/*,video/*"
                :disabled="uploading"
                @change="onUpload"
              />
            </label>
            <router-link
              v-else-if="!query && source === 'canva'"
              to="/ai/studio"
              class="btn btn-primary btn-sm mt-3"
            >
              {{ $t('storage.open_studio') }}
            </router-link>
          </div>

          <div v-else-if="viewMode === 'grid'" class="storage-grid">
            <article
              v-for="file in displayFiles"
              :key="file.id"
              class="storage-tile"
              @dblclick="openPreview(file)"
            >
              <button type="button" class="storage-tile__preview" @click="openPreview(file)">
                <img v-if="isImage(file)" :src="mediaSrc(file)" :alt="fileName(file)" loading="lazy" />
                <div v-else class="storage-tile__placeholder" :class="'storage-tile__placeholder--' + iconTone(file)">
                  <i :class="fileIcon(file)"></i>
                </div>
                <span class="storage-tile__badge">{{ sourceLabel(file.source) }}</span>
              </button>
              <div class="storage-tile__footer">
                <span class="storage-tile__name" :title="fileName(file)">{{ fileName(file) }}</span>
                <button
                  type="button"
                  class="storage-tile__menu"
                  :aria-label="$t('common.actions')"
                  @click.stop="toggleMenu(file, $event)"
                >
                  <i class="fas fa-ellipsis-v"></i>
                </button>
              </div>
            </article>
          </div>

          <div v-else class="table-responsive">
            <table class="table table-hover storage-table mb-0">
              <thead>
                <tr>
                  <th>{{ $t('common.name') }}</th>
                  <th>{{ $t('common.source') }}</th>
                  <th>{{ $t('common.size') }}</th>
                  <th>{{ $t('common.modified') }}</th>
                  <th class="text-right">{{ $t('global.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="file in displayFiles"
                  :key="file.id"
                  @dblclick="openPreview(file)"
                >
                  <td>
                    <button type="button" class="storage-table__name" @click="openPreview(file)">
                      <span class="storage-table__thumb">
                        <img v-if="isImage(file)" :src="mediaSrc(file)" :alt="fileName(file)" loading="lazy" />
                        <i v-else :class="fileIcon(file)"></i>
                      </span>
                      <span class="storage-table__label">{{ fileName(file) }}</span>
                    </button>
                  </td>
                  <td class="text-muted">{{ sourceLabel(file.source) }}</td>
                  <td class="text-muted">{{ formatBytes(file.size_bytes) }}</td>
                  <td class="text-muted">{{ formatDate(file.created_at) }}</td>
                  <td class="text-right">
                    <button
                      type="button"
                      class="btn btn-xs btn-outline-secondary"
                      :aria-label="$t('common.actions')"
                      @click.stop="toggleMenu(file, $event)"
                    >
                      <i class="fas fa-ellipsis-v"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </ContentCard>

    <div
      v-if="menuFile"
      class="storage-context"
      :style="menuStyle"
      role="menu"
      @click.stop
    >
      <button type="button" class="storage-menu__item" role="menuitem" @click="openPreview(menuFile); closeMenu()">
        <i class="fas fa-eye"></i> {{ $t('common.preview') }}
      </button>
      <button type="button" class="storage-menu__item" role="menuitem" @click="copyUrl(menuFile.url); closeMenu()">
        <i class="fas fa-link"></i> {{ $t('common.copy_link') }}
      </button>
      <button type="button" class="storage-menu__item storage-menu__item--danger" role="menuitem" @click="onDelete(menuFile); closeMenu()">
        <i class="fas fa-trash"></i> {{ $t('common.remove') }}
      </button>
    </div>

    <modal name="drive-preview-modal" height="auto" :adaptive="true" width="880">
      <div v-if="previewFile" class="storage-preview">
        <header class="storage-preview__header">
          <div class="storage-preview__title">
            <span class="storage-preview__icon" :class="'storage-preview__icon--' + iconTone(previewFile)">
              <i :class="fileIcon(previewFile)"></i>
            </span>
            <div class="storage-preview__meta">
              <h5 class="storage-preview__name">{{ fileName(previewFile) }}</h5>
              <p class="storage-preview__sub">
                {{ formatBytes(previewFile.size_bytes) }}
                · {{ sourceLabel(previewFile.source) }}
                · {{ formatDate(previewFile.created_at) }}
              </p>
            </div>
          </div>
          <div class="storage-preview__toolbar">
            <button type="button" class="storage-preview__btn" :title="$t('common.copy_link')" @click="copyUrl(previewFile.url)">
              <i class="fas fa-link"></i>
            </button>
            <a
              class="storage-preview__btn"
              :href="mediaSrc(previewFile)"
              target="_blank"
              rel="noopener noreferrer"
              :title="$t('storage.open_new_tab')"
            >
              <i class="fas fa-external-link-alt"></i>
            </a>
            <button type="button" class="storage-preview__btn storage-preview__btn--danger" :title="$t('common.remove')" @click="onDeleteFromPreview">
              <i class="fas fa-trash"></i>
            </button>
            <button type="button" class="storage-preview__btn" :title="$t('storage.close')" @click="$modal.hide('drive-preview-modal')">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </header>

        <div class="storage-preview__stage">
          <img v-if="isImage(previewFile)" :src="mediaSrc(previewFile)" :alt="fileName(previewFile)" />
          <video v-else-if="isVideo(previewFile)" :src="mediaSrc(previewFile)" controls />
          <div v-else class="storage-preview__unsupported">
            <i :class="fileIcon(previewFile)"></i>
            <p>{{ $t('storage.preview_unavailable') }}</p>
          </div>
        </div>

        <footer class="storage-preview__footer">
          <label class="storage-preview__link-label" for="storage-public-link">{{ $t('storage.public_link') }}</label>
          <div class="storage-preview__link-row">
            <input
              id="storage-public-link"
              class="form-control storage-preview__link-input"
              readonly
              :value="publicMediaUrl(previewFile.url)"
              @focus="$event.target.select()"
            />
            <button type="button" class="btn btn-primary" @click="copyUrl(previewFile.url)">
              <i class="fas fa-copy mr-1"></i> {{ $t('storage.copy') }}
            </button>
          </div>
        </footer>
      </div>
    </modal>
  </PageLayout>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { publicMediaUrl, resolveMediaUrl } from '@/utils/media'

export default {
  name: 'StorageDrive',
  data() {
    return {
      query: '',
      previewFile: null,
      uploading: false,
      deletingAll: false,
      viewMode: 'grid',
      sortBy: 'date_desc',
      sortOpen: false,
      dragOver: false,
      dragDepth: 0,
      menuFile: null,
      menuStyle: {},
    }
  },
  computed: {
    ...mapGetters('storage', ['files', 'usage', 'total', 'loading', 'source']),
    sourceTabs() {
      return [
        { id: '', label: this.$t('storage.my_files'), icon: 'fas fa-folder-open' },
        { id: 'upload', label: this.$t('storage.uploads_tab'), icon: 'fas fa-cloud-upload-alt' },
        { id: 'canva', label: this.$t('storage.canva_tab'), icon: 'fas fa-paint-brush' },
        { id: 'studio_api', label: this.$t('storage.studio_api_tab'), icon: 'fas fa-code' },
      ]
    },
    sortOptions() {
      return [
        { id: 'date_desc', label: this.$t('storage.last_modified') },
        { id: 'name_asc', label: this.$t('storage.name_az') },
        { id: 'size_desc', label: this.$t('storage.largest_first') },
      ]
    },
    activeSortLabel() {
      const match = this.sortOptions.find((opt) => opt.id === this.sortBy)
      return match ? match.label : this.$t('storage.last_modified')
    },
    filtered() {
      const q = this.query.trim().toLowerCase()
      if (!q) return this.files
      return this.files.filter((f) => fileName(f).toLowerCase().includes(q))
    },
    displayFiles() {
      const list = [...this.filtered]
      switch (this.sortBy) {
        case 'name_asc':
          return list.sort((a, b) => fileName(a).localeCompare(fileName(b)))
        case 'size_desc':
          return list.sort((a, b) => (b.size_bytes || 0) - (a.size_bytes || 0))
        default:
          return list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      }
    },
    usagePercent() {
      if (!this.usage.limit_bytes || this.usage.limit_bytes < 0) return 0
      return Math.min(100, Math.round((this.usage.used_bytes / this.usage.limit_bytes) * 100))
    },
    usageBarClass() {
      if (this.usagePercent >= 90) return 'storage-usage__fill--danger'
      if (this.usagePercent >= 70) return 'storage-usage__fill--warn'
      return ''
    },
    emptyTitle() {
      const map = {
        upload: this.$t('storage.no_uploads_yet'),
        canva: this.$t('storage.no_canva'),
        studio_api: this.$t('storage.no_studio_api'),
      }
      return map[this.source] || this.$t('storage.empty')
    },
    emptyHint() {
      const map = {
        upload: this.$t('storage.empty_upload_hint'),
        canva: this.$t('storage.empty_canva_hint'),
        studio_api: this.$t('storage.empty_studio_hint'),
      }
      return map[this.source] || this.$t('storage.empty_hint')
    },
    emptyIcon() {
      const map = {
        upload: 'fas fa-cloud-upload-alt',
        canva: 'fas fa-paint-brush',
        studio_api: 'fas fa-code',
      }
      return map[this.source] || 'fas fa-folder-open'
    },
    deleteAllLabel() {
      if (this.source === 'upload') return this.$t('storage.delete_all_uploads')
      if (this.source === 'canva') return this.$t('storage.delete_all_canva')
      if (this.source === 'studio_api') return this.$t('storage.delete_all_studio')
      return this.$t('storage.delete_all_files')
    },
  },
  async created() {
    await this.fetchFiles({ source: this.source })
    document.addEventListener('click', this.closeMenus)
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeMenus)
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    ...mapActions('storage', ['fetchFiles', 'uploadFile', 'deleteFile', 'deleteAllFiles']),
    setSource(source) {
      this.fetchFiles({ source })
    },
    setSort(id) {
      this.sortBy = id
      this.sortOpen = false
    },
    isImage(file) {
      const ct = (file.content_type || '').toLowerCase()
      return ct.startsWith('image/') || /\.(jpe?g|png|gif|webp)$/i.test(file.filename || '')
    },
    isVideo(file) {
      const ct = (file.content_type || '').toLowerCase()
      return ct.startsWith('video/') || /\.(mp4|mov|webm)$/i.test(file.filename || '')
    },
    fileIcon(file) {
      if (this.isVideo(file)) return 'fas fa-file-video'
      if (this.isImage(file)) return 'fas fa-file-image'
      return 'fas fa-file'
    },
    iconTone(file) {
      if (this.isVideo(file)) return 'video'
      return 'file'
    },
    sourceLabel(source) {
      const map = {
        upload: this.$t('storage.uploads_tab'),
        canva: this.$t('storage.canva_tab'),
        studio_api: this.$t('storage.studio_api_tab'),
      }
      return map[source] || this.$t('storage.my_files')
    },
    formatBytes(bytes) {
      const n = Number(bytes) || 0
      if (n < 1024) return `${n} B`
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
      if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`
      return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`
    },
    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    },
    fileName(file) {
      return file.original_name || file.filename || 'Untitled'
    },
    mediaSrc(file) {
      return resolveMediaUrl(file && file.url)
    },
    publicMediaUrl,
    openPreview(file) {
      this.previewFile = file
      this.$modal.show('drive-preview-modal')
    },
    toggleMenu(file, event) {
      if (this.menuFile && this.menuFile.id === file.id) {
        this.closeMenu()
        return
      }
      this.menuFile = file
      const x = event.clientX
      const y = event.clientY
      this.menuStyle = {
        top: `${Math.min(y, window.innerHeight - 160)}px`,
        left: `${Math.min(x, window.innerWidth - 200)}px`,
      }
    },
    closeMenu() {
      this.menuFile = null
    },
    closeMenus() {
      this.menuFile = null
      this.sortOpen = false
    },
    onKeydown(e) {
      if (e.key === 'Escape') this.closeMenus()
    },
    onDragEnter() {
      this.dragDepth += 1
      this.dragOver = true
    },
    onDragOver() {
      this.dragOver = true
    },
    onDragLeave() {
      this.dragDepth -= 1
      if (this.dragDepth <= 0) {
        this.dragDepth = 0
        this.dragOver = false
      }
    },
    async onDrop(event) {
      this.dragDepth = 0
      this.dragOver = false
      const files = Array.from(event.dataTransfer.files || [])
      if (!files.length) return
      await this.uploadFiles(files)
    },
    async copyUrl(url) {
      try {
        await navigator.clipboard.writeText(publicMediaUrl(url))
        this.$toast.success(this.$t('storage.link_copied'))
      } catch (e) {
        this.$toast.error(this.$t('storage.copy_failed'))
      }
    },
    async uploadFiles(files) {
      this.uploading = true
      try {
        for (const file of files) {
          await this.uploadFile(file)
        }
        this.$toast.success(files.length > 1
          ? this.$t('storage.files_uploaded', { count: files.length })
          : this.$t('storage.file_uploaded'))
      } catch (e) {
        const status = e.response && e.response.status
        const msg = (e.response && e.response.data && e.response.data.message) || 'Upload failed'
        if (status === 402) this.$toast.error(this.$t('storage.storage_full_toast'))
        else this.$toast.error(msg)
      } finally {
        this.uploading = false
      }
    },
    async onUpload(event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      if (!files.length) return
      await this.uploadFiles(files)
    },
    async onDeleteAll() {
      const count = this.total
      if (!count) return
      const msg = this.$t('storage.delete_all_confirm', { count, scope: this.deleteAllLabel })
      const okAll = await this.$confirm(msg, { confirmText: this.$t('common.delete'), danger: true })
      if (!okAll) return
      this.deletingAll = true
      try {
        const result = await this.deleteAllFiles()
        const deleted = Number((result && result.deleted) || count)
        this.$toast.success(this.$t('storage.delete_all_done', { count: deleted }))
      } catch (e) {
        this.$toast.error((e.response && e.response.data && e.response.data.message) || this.$t('storage.delete_failed'))
      } finally {
        this.deletingAll = false
      }
    },
    async onDelete(file) {
      const name = this.fileName(file)
      const ok = await this.$confirm(this.$t('storage.delete_confirm', { name }), {
        confirmText: this.$t('common.delete'),
        danger: true,
      })
      if (!ok) return
      try {
        await this.deleteFile(file.id)
        this.$toast.success(this.$t('storage.file_removed'))
      } catch (e) {
        this.$toast.error((e.response && e.response.data && e.response.data.message) || this.$t('storage.delete_failed'))
      }
    },
    async onDeleteFromPreview() {
      const file = this.previewFile
      if (!file) return
      this.$modal.hide('drive-preview-modal')
      await this.onDelete(file)
    },
  },
}

function fileName(file) {
  return file.original_name || file.filename || 'Untitled'
}
</script>

<style scoped>
.storage-page >>> .breadcrumb-main {
  margin-bottom: 0.75rem;
}

.storage-page >>> .contents {
  padding-bottom: 1rem;
}

.storage-page >>> .contents > .container {
  max-width: none;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.storage-page >>> .storage-card {
  margin-bottom: 0 !important;
}

.storage-upload-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  overflow: hidden;
  pointer-events: none;
}

.storage-usage {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1rem;
  margin-bottom: 0.75rem;
  padding: 0.65rem 0.85rem;
  background: var(--wc-bg-card);
  border: 1px solid var(--wc-border);
  border-radius: var(--wc-radius-lg);
  box-shadow: var(--wc-shadow-sm);
}

.storage-usage__body {
  flex: 1;
  min-width: 200px;
}

.storage-usage__top {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.35rem 1rem;
  margin-bottom: 0.5rem;
}

.storage-usage__label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--wc-text-heading);
}

.storage-usage__meta,
.storage-usage__count {
  font-size: 0.8125rem;
  color: var(--wc-text-muted);
}

.storage-usage__track {
  height: 6px;
  background: var(--wc-bg-subtle);
  border-radius: var(--wc-radius-sm);
  overflow: hidden;
}

.storage-usage__fill {
  height: 100%;
  background: var(--wc-primary);
  border-radius: var(--wc-radius-sm);
  transition: width 0.25s ease;
}

.storage-usage__fill--warn {
  background: var(--wc-warning);
}

.storage-usage__fill--danger {
  background: var(--wc-danger);
}

.storage-usage__upgrade {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--wc-primary);
  text-decoration: none;
  white-space: nowrap;
}

.storage-usage__upgrade:hover {
  text-decoration: underline;
}

.storage-card {
  margin-bottom: 0 !important;
}

.storage-panel {
  position: relative;
  min-height: 0;
}

.storage-panel--drag {
  outline: 2px dashed var(--wc-primary);
  outline-offset: -4px;
}

.storage-toolbar {
  padding: 0.75rem 1rem 0.65rem;
  border-bottom: 1px solid var(--wc-border);
  background: var(--wc-bg-card);
}

.storage-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.65rem;
}

.storage-filter {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.85rem;
  border: 1px solid var(--wc-border);
  border-radius: var(--wc-radius-md);
  background: var(--wc-bg-card);
  color: var(--wc-text);
  font-size: 0.8125rem;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}

.storage-filter i {
  font-size: 0.75rem;
  color: var(--wc-text-muted);
}

.storage-filter:hover {
  border-color: rgba(var(--wc-primary-rgb), 0.35);
  background: var(--wc-primary-light);
}

.storage-filter--active {
  border-color: var(--wc-primary);
  background: var(--wc-primary-light);
  color: var(--wc-primary-dark);
  font-weight: 500;
}

.storage-filter--active i {
  color: var(--wc-primary);
}

.storage-filter__count {
  min-width: 1.25rem;
  padding: 0 0.35rem;
  border-radius: var(--wc-radius-sm);
  background: rgba(var(--wc-primary-rgb), 0.12);
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.4;
  text-align: center;
}

.storage-toolbar__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
}

.storage-search {
  flex: 1;
  min-width: min(100%, 220px);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 40px;
  padding: 0 0.75rem;
  border: 1px solid var(--wc-border);
  border-radius: var(--wc-radius-md);
  background: var(--wc-bg-card);
}

.storage-search:focus-within {
  border-color: var(--wc-primary);
  box-shadow: 0 0 0 3px rgba(var(--wc-primary-rgb), 0.12);
}

.storage-search i {
  color: var(--wc-text-muted);
  font-size: 0.8125rem;
}

.storage-search input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  font-size: 0.875rem;
  color: var(--wc-text);
  outline: none;
}

.storage-search input::placeholder {
  color: var(--wc-text-muted);
}

.storage-search__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: var(--wc-radius-sm);
  background: transparent;
  color: var(--wc-text-muted);
  cursor: pointer;
}

.storage-search__clear:hover {
  background: var(--wc-bg-subtle);
  color: var(--wc-text);
}

.storage-toolbar__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
}

.storage-sort {
  position: relative;
}

.storage-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 180px;
  background: var(--wc-bg-card);
  border: 1px solid var(--wc-border);
  border-radius: var(--wc-radius-md);
  box-shadow: var(--wc-shadow);
  padding: 0.35rem 0;
  z-index: 20;
}

.storage-menu__item {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.5rem 0.85rem;
  border: none;
  background: transparent;
  color: var(--wc-text);
  font-size: 0.8125rem;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  white-space: nowrap;
}

.storage-menu__item:hover {
  background: var(--wc-bg-subtle);
}

.storage-menu__item--active {
  color: var(--wc-primary);
  font-weight: 500;
}

.storage-menu__item--danger {
  color: var(--wc-danger);
}

.storage-menu__item i {
  width: 1rem;
  color: var(--wc-text-muted);
}

.storage-drop {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(var(--wc-primary-rgb), 0.08);
  pointer-events: none;
}

.storage-drop i {
  font-size: 2.5rem;
  color: var(--wc-primary);
  margin-bottom: 0.75rem;
}

.storage-drop p {
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  color: var(--wc-text-heading);
}

.storage-body {
  padding: 0.75rem 1rem 1rem;
  min-height: 0;
}

.storage-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
  padding: 1.5rem 0;
  text-align: center;
  color: var(--wc-text-muted);
}

.storage-state i.fa-spin {
  font-size: 1.5rem;
  margin-bottom: 0.75rem;
  color: var(--wc-primary);
}

.storage-state__icon {
  width: 2.75rem;
  height: 2.75rem;
  margin-bottom: 0.65rem;
  border-radius: var(--wc-radius-lg);
  background: var(--wc-primary-light);
  color: var(--wc-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.storage-state h5 {
  margin: 0 0 0.35rem;
  font-size: 1rem;
  font-weight: 600;
  color: var(--wc-text-heading);
}

.storage-state p {
  max-width: 26rem;
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
}

.storage-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(168px, 1fr));
  gap: 1rem 0.85rem;
}

.storage-tile {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.storage-tile__preview {
  position: relative;
  display: block;
  width: 100%;
  padding: 0;
  border: 1px solid var(--wc-border);
  border-radius: var(--wc-radius-lg);
  background: var(--wc-bg-subtle);
  overflow: hidden;
  cursor: pointer;
  aspect-ratio: 1;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.storage-tile__preview:hover {
  border-color: rgba(var(--wc-primary-rgb), 0.35);
  box-shadow: var(--wc-shadow-sm);
}

.storage-tile__preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.storage-tile__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 2rem;
  color: var(--wc-text-muted);
}

.storage-tile__placeholder--video {
  color: var(--wc-danger);
}

.storage-tile__badge {
  position: absolute;
  left: 0.5rem;
  bottom: 0.5rem;
  padding: 0.15rem 0.45rem;
  border-radius: var(--wc-radius-sm);
  background: rgba(12, 47, 94, 0.78);
  color: #fff;
  font-size: 0.625rem;
  font-weight: 500;
  text-transform: capitalize;
}

.storage-tile__footer {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem 0.15rem 0;
  min-width: 0;
}

.storage-tile__name {
  flex: 1;
  min-width: 0;
  font-size: 0.8125rem;
  color: var(--wc-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.storage-tile__menu {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: var(--wc-radius-sm);
  background: transparent;
  color: var(--wc-text-muted);
  cursor: pointer;
}

.storage-tile__menu:hover {
  background: var(--wc-bg-subtle);
  color: var(--wc-text);
}

.storage-table__name {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  max-width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.storage-table__thumb {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: var(--wc-radius-sm);
  overflow: hidden;
  background: var(--wc-bg-subtle);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.storage-table__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.storage-table__thumb i {
  font-size: 0.875rem;
  color: var(--wc-text-muted);
}

.storage-table__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.storage-context {
  position: fixed;
  z-index: 100;
  min-width: 180px;
  background: var(--wc-bg-card);
  border: 1px solid var(--wc-border);
  border-radius: var(--wc-radius-md);
  box-shadow: var(--wc-shadow);
  padding: 0.35rem 0;
}

.storage-preview {
  background: var(--wc-bg-card);
  border-radius: var(--wc-radius-xl);
  overflow: hidden;
  color: var(--wc-text);
}

.storage-preview__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--wc-border);
}

.storage-preview__title {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
  flex: 1;
}

.storage-preview__icon {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: var(--wc-radius-lg);
  background: var(--wc-primary-light);
  color: var(--wc-primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.storage-preview__icon--video {
  background: rgba(239, 110, 110, 0.12);
  color: var(--wc-danger);
}

.storage-preview__icon--file {
  background: var(--wc-bg-subtle);
  color: var(--wc-text-muted);
}

.storage-preview__meta {
  min-width: 0;
}

.storage-preview__name {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--wc-text-heading);
}

.storage-preview__sub {
  margin: 0.2rem 0 0;
  font-size: 0.75rem;
  color: var(--wc-text-muted);
}

.storage-preview__toolbar {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
}

.storage-preview__btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: var(--wc-radius-md);
  background: transparent;
  color: var(--wc-text-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s, color 0.15s;
}

.storage-preview__btn:hover {
  background: var(--wc-bg-subtle);
  color: var(--wc-text);
}

.storage-preview__btn--danger:hover {
  background: rgba(239, 110, 110, 0.12);
  color: var(--wc-danger);
}

.storage-preview__stage {
  background: var(--wc-primary-dark);
  min-height: 280px;
  max-height: min(62vh, 640px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  overflow: auto;
}

.storage-preview__stage img,
.storage-preview__stage video {
  max-width: 100%;
  max-height: min(58vh, 600px);
  border-radius: var(--wc-radius-sm);
}

.storage-preview__unsupported {
  text-align: center;
  color: rgba(255, 255, 255, 0.65);
  padding: 2rem 1rem;
}

.storage-preview__unsupported i {
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
  opacity: 0.8;
}

.storage-preview__unsupported p {
  margin: 0;
  font-size: 0.875rem;
}

.storage-preview__footer {
  padding: 1rem 1.25rem 1.25rem;
  border-top: 1px solid var(--wc-border);
  background: var(--wc-bg-subtle);
}

.storage-preview__link-label {
  display: block;
  margin: 0 0 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--wc-text-muted);
}

.storage-preview__link-row {
  display: flex;
  gap: 0.65rem;
  align-items: stretch;
}

.storage-preview__link-input {
  font-size: 0.8125rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

@media (max-width: 767px) {
  .storage-toolbar {
    padding: 0.65rem 0.75rem 0.5rem;
  }

  .storage-body {
    padding: 0.65rem 0.75rem 0.75rem;
  }

  .storage-toolbar__row {
    flex-direction: column;
    align-items: stretch;
  }

  .storage-toolbar__actions {
    margin-left: 0;
    justify-content: flex-end;
  }

  .storage-usage {
    flex-direction: column;
    align-items: stretch;
  }

  .storage-preview__header {
    flex-wrap: wrap;
  }

  .storage-preview__link-row {
    flex-direction: column;
  }

  .storage-table thead th:nth-child(2),
  .storage-table thead th:nth-child(3),
  .storage-table tbody td:nth-child(2),
  .storage-table tbody td:nth-child(3) {
    display: none;
  }
}
</style>
